import { FC, useState, useRef, memo } from "react";
import { Box, Button, Typography, useTheme, Tooltip } from "@mui/material";
import { darken, lighten } from "@mui/material/styles";
import { InProgressLocationIcon, makeSxStyles } from "ui";
import {
  paletteColorStringRenderer,
  formatNumberToMinutes,
  formatNumberToMinutesPrecise,
} from "../../../../utils/string-helpers";
import {
  getAddressBoxStyleDetails,
  getDarkenedColor,
} from "../../../../shared/utils/address-box-styling";
import { TIMETABLE_ITEM_WIDTH } from "../../route-planner/helpers/timetable";
import useTimetableZoomStore from "../../../../state/timetable-zoom-store";
import { RouteStatus } from "../../route-planner/helpers/route-status.enum";

export interface AddressBoxProps {
  duration: number;
  color: string | undefined;
  last?: boolean;
  first?: boolean;
  selected?: boolean;
  status?: string;
  onAddressClick: (e: React.MouseEvent) => void;
  onMouseEnter?: (e: React.MouseEvent) => void;
  onMouseLeave?: () => void;
  hasDynamicWidth?: boolean;
  locationSequence: number;
  serviceTime?: number;
  travelTimeBetweenLocations?: number;
  travelTimeFromStartingPoint?: number; // Travel time from white dot to first location
  adjustedServiceTime?: number; // Adjusted service time when actual exceeds estimate
  adjustedTravelTime?: number; // Adjusted travel time when actual exceeds estimate
  // New props for line hover functionality
  onLineMouseEnter?: (e: React.MouseEvent) => void;
  onLineMouseLeave?: () => void;
  isSourceAddressDarkened?: boolean;
  isDestinationAddressDarkened?: boolean;
  isLineDarkened?: boolean;
  isUnassignedRoute?: boolean;
  currentRouteStatus?: RouteStatus;
  stackShift?: number; // Leftward px offset to fan out overlapping completed zero-service boxes
}

const LocationAddressComponent: FC<AddressBoxProps> = (props): JSX.Element => {
  const styles = useStyles();
  const { zoomLevel } = useTimetableZoomStore();
  const {
    duration,
    color,
    status,
    last,
    first,
    selected,
    onAddressClick,
    onMouseEnter,
    onMouseLeave,
    hasDynamicWidth,
    locationSequence,
    serviceTime,
    travelTimeBetweenLocations,
    travelTimeFromStartingPoint,
    adjustedServiceTime,
    adjustedTravelTime,
    onLineMouseEnter,
    onLineMouseLeave,
    isSourceAddressDarkened,
    isDestinationAddressDarkened,
    isLineDarkened,
    isUnassignedRoute = false,
    currentRouteStatus,
    stackShift,
  } = props;
  const { palette } = useTheme();

  // Orders with 0 service time get a minimum width for visibility.
  // Apply negative margin so they don't consume additional timeline space.
  const effectiveServiceTimeForCheck = adjustedServiceTime ?? serviceTime;
  const isZeroServiceTimeOrder = effectiveServiceTimeForCheck === 0;

  const isDoneStatus = ["completed", "partiallyCompleted", "cantPlow"].includes(
    status || "",
  );

  // A completed (done) zero-service stop should sit BEHIND the current-time
  // indicator (the service is finished), so its negative margin goes on the left
  // and its end aligns with its time position instead of extending into the future.
  const isDoneZeroServiceOrder = isZeroServiceTimeOrder && isDoneStatus;

  // A done stop with real width (e.g. driver-started then admin-completed)
  // can be painted over by the leftward overhang of neighbouring zero-service
  // cards (zIndex 1) — raise it above them so the serviced order never
  // disappears. Hovering a card still lifts the card to the top (zIndex 5).
  const isDoneWithServiceOrder = !isZeroServiceTimeOrder && isDoneStatus;

  // Track mouse position for tooltip positioning
  // Use separate state for each tooltip to avoid conflicts
  const [startingPointMousePosition, setStartingPointMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const [regularLineMousePosition, setRegularLineMousePosition] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const travelTimeAreaRef = useRef<HTMLDivElement>(null);
  const startingPointAreaRef = useRef<HTMLDivElement>(null);

  // Handle mouse move for starting point tooltip
  const handleStartingPointMouseMove = (
    e: React.MouseEvent<HTMLDivElement>,
  ) => {
    setStartingPointMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleStartingPointMouseLeave = () => {
    setStartingPointMousePosition(null);
  };

  // Handle mouse move for regular line tooltip
  const handleRegularLineMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    setRegularLineMousePosition({ x: e.clientX, y: e.clientY });
  };

  const handleRegularLineMouseLeave = () => {
    setRegularLineMousePosition(null);
  };

  const paletteColorType = paletteColorStringRenderer(
    palette,
    color?.toLowerCase(),
    false,
    status,
  );

  // Selected boxes (e.g. clicked on the map) are filled solid with a slightly
  // brighter shade of their own color, so they stand out from the outlined
  // boxes around them on any route color or status.
  const finalColor = selected
    ? lighten(paletteColorType || "#cccccc", 0.15)
    : paletteColorType;

  // lighten() returns an rgb() string, which getDarkenedColor can't parse, so
  // selected boxes are darkened with MUI's darken() instead
  const isAddressDarkened =
    isSourceAddressDarkened || isDestinationAddressDarkened;
  const addressColor = !isAddressDarkened
    ? finalColor
    : selected
      ? darken(finalColor || "#cccccc", 0.3)
      : getDarkenedColor(finalColor || "#cccccc");

  // The travel line keeps the route's normal color even when the box is selected
  const lineColor = isLineDarkened
    ? getDarkenedColor(paletteColorType || "#cccccc")
    : paletteColorType;

  // Use adjusted service time if available, otherwise use original service time
  const effectiveServiceTime = adjustedServiceTime ?? serviceTime;

  // In live phase, use precise (non-rounded) calculations
  const isLivePhase = currentRouteStatus === RouteStatus.Live;

  const { shouldShowLocationSequence, boxWidth } = getAddressBoxStyleDetails(
    duration,
    locationSequence,
    hasDynamicWidth || false,
    status || "",
    zoomLevel,
    effectiveServiceTime,
    currentRouteStatus,
  );

  // Calculate pixel width per minute based on zoom level
  // At zoom 1.0: TIMETABLE_ITEM_WIDTH = 60px per hour, so 1px per minute
  // Formula: (hourWidth / 60) * zoomLevel
  const pixelsPerMinute = (TIMETABLE_ITEM_WIDTH / 60) * zoomLevel;

  // Use adjusted travel time if available, otherwise use original travel time
  const effectiveTravelTimeBetweenLocations =
    adjustedTravelTime ?? travelTimeBetweenLocations;

  // Calculate the spacing width based on travelTimeBetweenLocations
  // In planning phase, no spacing between addresses
  // In live/planned phases, use travel time-based spacing (no minimum)
  // Skipped addresses should not be affected by zoom
  // In live phase, use precise (non-rounded) width for accurate timeline representation
  const spacingWidth =
    status === "skipped"
      ? 4 // Skipped addresses always use 4px spacing, no zoom
      : hasDynamicWidth &&
          currentRouteStatus !== RouteStatus.Planning &&
          effectiveTravelTimeBetweenLocations &&
          effectiveTravelTimeBetweenLocations > 0
        ? isLivePhase
          ? effectiveTravelTimeBetweenLocations * pixelsPerMinute // Precise width in live phase
          : Math.round(effectiveTravelTimeBetweenLocations * pixelsPerMinute) // Round in other phases
        : 0; // No default spacing - boxes fit proportionally within timeline

  const zeroServiceMarginPx =
    currentRouteStatus === RouteStatus.Planning
      ? boxWidth
      : Math.min(boxWidth, spacingWidth);

  // Calculate the spacing width from starting point to first location
  // Only apply this to the first location if it has travel time from starting point
  // In live phase, use precise (non-rounded) width
  const startingPointSpacingWidth =
    first &&
    hasDynamicWidth &&
    currentRouteStatus !== RouteStatus.Planning &&
    travelTimeFromStartingPoint &&
    travelTimeFromStartingPoint > 0
      ? isLivePhase
        ? travelTimeFromStartingPoint * pixelsPerMinute // Precise width in live phase
        : Math.round(travelTimeFromStartingPoint * pixelsPerMinute) // Round in other phases
      : 0; // No default spacing

  return (
    <Box
      sx={{
        ...styles.addressWrapper,
        ...(isUnassignedRoute && !last && { marginRight: "4px" }),
      }}
    >
      {first &&
        !isUnassignedRoute &&
        !!travelTimeFromStartingPoint &&
        travelTimeFromStartingPoint > 0 && (
          <Tooltip
            title={`Travel Time: ${isLivePhase ? formatNumberToMinutesPrecise(travelTimeFromStartingPoint) : formatNumberToMinutes(travelTimeFromStartingPoint)}`}
            arrow
            placement="top"
            open={
              startingPointMousePosition !== null &&
              startingPointAreaRef.current !== null
            }
            PopperProps={{
              anchorEl: startingPointMousePosition
                ? {
                    getBoundingClientRect: () => ({
                      top: startingPointMousePosition.y,
                      left: startingPointMousePosition.x,
                      right: startingPointMousePosition.x,
                      bottom: startingPointMousePosition.y,
                      width: 0,
                      height: 0,
                      x: startingPointMousePosition.x,
                      y: startingPointMousePosition.y,
                      toJSON: () => ({}),
                    }),
                  }
                : null,
            }}
          >
            <Box
              ref={startingPointAreaRef}
              sx={styles.travelTimeArea}
              style={{
                width: `${startingPointSpacingWidth}px`,
              }}
              onMouseMove={handleStartingPointMouseMove}
              onMouseLeave={handleStartingPointMouseLeave}
            >
              <Box
                className="startingPointLine"
                sx={styles.addressLine}
                style={{
                  backgroundColor: "#fff",
                }}
              />
            </Box>
          </Tooltip>
        )}
      <Button
        sx={{
          ...styles.addressBox,
          // Borders impose a minimum width even with border-box sizing. Use an
          // outline for every sub-2px service so short recorded durations do
          // not accumulate extra layout width and push finished stops ahead.
          ...(boxWidth < 2
            ? {
                border: "none",
                outline:
                  status === "inProgress"
                    ? "1px solid #454749"
                    : `1px solid ${addressColor}`,
                outlineOffset: "0px",
              }
            : {
                border:
                  status === "inProgress"
                    ? "1px solid #454749"
                    : `1px solid ${addressColor}`,
              }),
          // For 0 service time orders, use negative margin to avoid consuming timeline space.
          // The box remains visible but doesn't push subsequent addresses.
          // Done stops shift LEFT (box ends at its time position, behind the
          // current-time indicator); upcoming stops keep the original right shift.
          ...(isZeroServiceTimeOrder && {
            ...(isDoneZeroServiceOrder
              ? { marginLeft: `-${boxWidth}px` }
              : { marginRight: `-${zeroServiceMarginPx}px` }),
            zIndex: 1, // Ensure it appears above the travel line
          }),
          // Keep serviced done stops above overhanging zero-service cards
          ...(isDoneWithServiceOrder && { zIndex: 2 }),
          // Deck-of-cards stagger for runs of overlapping completed
          // zero-service stops: translate is paint-only, so the timeline
          // flow (and everything after the run) is unaffected. Later DOM
          // order keeps the newest box on top; hover raises a covered box.
          // A left-falling shadow (plus the opaque fill in `style` below)
          // makes each card visibly sit on the one beneath it.
          ...(stackShift !== undefined && {
            boxShadow: "-3px 0 5px rgba(0, 0, 0, 0.5)",
            "&:hover": { zIndex: 5 },
            ...(stackShift > 0 && {
              transform: `translateX(-${stackShift}px)`,
            }),
          }),
          // Keep a selected box above neighbouring and stacked boxes. Short
          // stops can be only a couple of pixels wide, so the solid fill is
          // drawn at least 10px wide, centered on the stop. It sits behind the
          // sequence number (z-index -1 inside the box's stacking context).
          ...(selected && {
            zIndex: 6,
            overflow: "visible",
            "&::before": {
              content: '""',
              position: "absolute",
              top: "-1px",
              bottom: "-1px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "max(calc(100% + 2px), 10px)",
              borderRadius: "3px",
              backgroundColor: addressColor,
              zIndex: -1,
              pointerEvents: "none",
            },
          }),
        }}
        style={{
          width: `${boxWidth}px`, // Explicitly set as pixels to avoid any interpretation issues
          // Stacked cards get the timeline's background color so each card
          // occludes the one beneath instead of showing through it.
          backgroundColor: selected
            ? addressColor
            : stackShift !== undefined
              ? "#1E2022"
              : "transparent",
        }}
        onClick={(e) => {
          e.stopPropagation();
          onAddressClick(e);
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
        data-testid="location-address"
      >
        {shouldShowLocationSequence &&
          locationSequence > 0 &&
          !isUnassignedRoute && (
            <Typography
              sx={{
                ...styles.locationSequence,
                // Dark number on the solid fill of a selected box
                color: selected ? "#1E2022" : addressColor,
              }}
            >
              {locationSequence}
            </Typography>
          )}
        {status === "inProgress" && !isUnassignedRoute && (
          <InProgressLocationIcon width="10" height="10" />
        )}
      </Button>
      {!isUnassignedRoute ? (
        <Tooltip
          title={
            effectiveTravelTimeBetweenLocations &&
            effectiveTravelTimeBetweenLocations > 0
              ? `Travel Time: ${isLivePhase ? formatNumberToMinutesPrecise(effectiveTravelTimeBetweenLocations) : formatNumberToMinutes(effectiveTravelTimeBetweenLocations)}`
              : ""
          }
          arrow
          placement="top"
          // Control tooltip visibility manually based on mouse position
          open={
            regularLineMousePosition !== null &&
            travelTimeAreaRef.current !== null &&
            effectiveTravelTimeBetweenLocations !== undefined &&
            effectiveTravelTimeBetweenLocations > 0
          }
          PopperProps={{
            anchorEl: regularLineMousePosition
              ? {
                  getBoundingClientRect: () => ({
                    top: regularLineMousePosition.y,
                    left: regularLineMousePosition.x,
                    right: regularLineMousePosition.x,
                    bottom: regularLineMousePosition.y,
                    width: 0,
                    height: 0,
                    x: regularLineMousePosition.x,
                    y: regularLineMousePosition.y,
                    toJSON: () => ({}),
                  }),
                }
              : null,
          }}
        >
          <Box
            ref={travelTimeAreaRef}
            sx={styles.travelTimeArea}
            style={{
              width: `${spacingWidth}px`,
            }}
            onMouseEnter={onLineMouseEnter}
            onMouseLeave={() => {
              if (onLineMouseLeave) onLineMouseLeave();
              handleRegularLineMouseLeave();
            }}
            onMouseMove={handleRegularLineMouseMove}
          >
            {/* Only show the line if it's not the last item */}
            {!last && (
              <Box
                className="addressLine"
                sx={styles.addressLine}
                style={{
                  backgroundColor: lineColor,
                }}
              />
            )}
          </Box>
        </Tooltip>
      ) : null}
    </Box>
  );
};

const useStyles = makeSxStyles(() => ({
  addressWrapper: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    gap: 0, // Ensure no gap between elements
  },
  addressBox: {
    height: "20px",
    borderRadius: "3px",
    minWidth: "unset",
    padding: "unset",
    boxSizing: "border-box", // Include borders in width calculation to prevent pixel loss
    flexShrink: 0, // Prevent flexbox from shrinking the box
    flexGrow: 0, // Prevent flexbox from growing the box
    flexBasis: "auto", // Use the explicit width value
  },
  travelTimeArea: {
    height: "20px", // Make the hoverable area the same height as the address box
    display: "flex",
    alignItems: "center",
    cursor: "pointer",
    flexShrink: 0, // Prevent flexbox from shrinking the travel time area
    flexGrow: 0, // Prevent flexbox from growing the travel time area
    flexBasis: "auto", // Use the explicit width value
    "&:hover .addressLine": {
      height: "2px", // Make the line slightly thicker on hover for better UX
    },
  },
  addressLine: {
    height: "1px",
    width: "100%",
    transition: "height 0.1s ease",
  },
  locationSequence: {
    fontSize: "11px",
    fontWeight: 500,
  },
}));

// Custom comparison function for memo - only re-render when meaningful props change
const arePropsEqual = (
  prevProps: AddressBoxProps,
  nextProps: AddressBoxProps,
): boolean => {
  return (
    prevProps.duration === nextProps.duration &&
    prevProps.color === nextProps.color &&
    prevProps.last === nextProps.last &&
    prevProps.first === nextProps.first &&
    prevProps.selected === nextProps.selected &&
    prevProps.status === nextProps.status &&
    prevProps.hasDynamicWidth === nextProps.hasDynamicWidth &&
    prevProps.locationSequence === nextProps.locationSequence &&
    prevProps.serviceTime === nextProps.serviceTime &&
    prevProps.travelTimeBetweenLocations ===
      nextProps.travelTimeBetweenLocations &&
    prevProps.travelTimeFromStartingPoint ===
      nextProps.travelTimeFromStartingPoint &&
    prevProps.adjustedServiceTime === nextProps.adjustedServiceTime &&
    prevProps.adjustedTravelTime === nextProps.adjustedTravelTime &&
    prevProps.isSourceAddressDarkened === nextProps.isSourceAddressDarkened &&
    prevProps.isDestinationAddressDarkened ===
      nextProps.isDestinationAddressDarkened &&
    prevProps.isLineDarkened === nextProps.isLineDarkened &&
    prevProps.isUnassignedRoute === nextProps.isUnassignedRoute &&
    prevProps.currentRouteStatus === nextProps.currentRouteStatus &&
    prevProps.stackShift === nextProps.stackShift
  );
};

export default memo(LocationAddressComponent, arePropsEqual);
